import { useMemo } from "react";

import isNil from "lodash/isNil";

import { ProfileTestID } from "@/constants/TestID";
import MultiSelectTextField from "@/ui/composed/pickers/MultiSelectTextField";
import { LabeledInput, TextField } from "@/ui/core";
import type { PermissionChecker } from "@/utils/profile/permissions.util";
import { isEditable, isViewable } from "@/utils/profile/permissions.util";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = ProfileTestID.BasicInfo;

const FirstNameField = ({
  firstName,
  setFirstName,
  trackFieldPressIn,
  userPermissions,
}: {
  firstName: string;
  setFirstName: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("firstName"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="First name"
        testID={joinTestIDs(baseTestID, "first-name-label")}
      >
        <TextField
          isDisabled={!isEditable(userPermissions("firstName"))}
          onChange={(value) => {
            setFirstName(value);
          }}
          onPressIn={() => trackFieldPressIn?.(FirstNameField.name)}
          placeholder="First name"
          testID={joinTestIDs(baseTestID, "first-name")}
          value={firstName}
        />
      </LabeledInput>
    );
  }, [firstName, setFirstName, userPermissions, trackFieldPressIn]);
};

const LastNameField = ({
  lastName,
  setLastName,
  trackFieldPressIn,
  userPermissions,
}: {
  lastName: string;
  setLastName: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("lastName"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="Last name"
        testID={joinTestIDs(baseTestID, "last-name-label")}
      >
        <TextField
          isDisabled={!isEditable(userPermissions("lastName"))}
          onChange={(value) => {
            setLastName(value);
          }}
          onPressIn={() => trackFieldPressIn?.(LastNameField.name)}
          placeholder="Last name"
          testID={joinTestIDs(baseTestID, "last-name")}
          value={lastName}
        />
      </LabeledInput>
    );
  }, [lastName, setLastName, userPermissions, trackFieldPressIn]);
};

const ChosenNameField = ({
  chosenName,
  setChosenName,
  trackFieldPressIn,
  userPermissions,
}: {
  chosenName?: string;
  setChosenName: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("chosenName"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="Chosen name"
        rightText="Optional"
        testID={joinTestIDs(baseTestID, "chosen-name-label")}
      >
        <TextField
          isDisabled={!isEditable(userPermissions("chosenName"))}
          onChange={(value) => {
            setChosenName(value);
          }}
          onPressIn={() => trackFieldPressIn?.(ChosenNameField.name)}
          placeholder="Chosen name"
          testID={joinTestIDs(baseTestID, "chosen-name")}
          value={chosenName}
        />
      </LabeledInput>
    );
  }, [chosenName, setChosenName, userPermissions, trackFieldPressIn]);
};

const GenderField = ({
  gender,
  options,
  setGender,
  trackFieldPressIn,
  userPermissions,
}: {
  gender: string;
  options: MultiSelectOption[];
  setGender: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("gender"))) {
      return null;
    }

    return (
      <LabeledInput
        leftText="Gender"
        testID={joinTestIDs(baseTestID, "gender-label")}
      >
        <MultiSelectTextField
          defaultSelectedValues={[{ displayText: gender, value: gender }]}
          isDisabled={!isEditable(userPermissions("gender"))}
          isSingleSelect
          modalTitle="Gender"
          onPressIn={() => trackFieldPressIn?.(GenderField.name)}
          onValuesChange={(selection) =>
            setGender(selection.length > 0 ? selection[0].value : "")
          }
          options={options}
          testID={joinTestIDs(baseTestID, "gender")}
          value={gender}
        />
      </LabeledInput>
    );
  }, [gender, options, setGender, userPermissions, trackFieldPressIn]);
};

const PronounsField = ({
  options,
  pronouns,
  setPronouns,
  trackFieldPressIn,
  userPermissions,
}: {
  options: MultiSelectOption[];
  pronouns?: string[];
  setPronouns: (value: string[]) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("pronouns"))) {
      return null;
    }

    const defaultSelectedValues = isNil(pronouns)
      ? []
      : pronouns.map((pronoun: string) => ({
          displayText: pronoun,
          value: pronoun,
        }));

    return (
      <LabeledInput
        leftText="Pronouns"
        testID={joinTestIDs(baseTestID, "pronouns-label")}
      >
        <MultiSelectTextField
          defaultSelectedValues={defaultSelectedValues}
          isDisabled={!isEditable(userPermissions("pronouns"))}
          modalTitle="Pronouns"
          onPressIn={() => trackFieldPressIn?.(PronounsField.name)}
          onValuesChange={(selections) =>
            setPronouns(
              selections.map((option: MultiSelectOption) => option.value),
            )
          }
          options={options}
          testID={joinTestIDs(baseTestID, "pronouns")}
          value={pronouns}
        />
      </LabeledInput>
    );
  }, [options, pronouns, setPronouns, userPermissions, trackFieldPressIn]);
};

export {
  ChosenNameField,
  FirstNameField,
  GenderField,
  LastNameField,
  PronounsField,
};
